// Re-skin shadcn de packages/hce/src/hce/copiaHistoria/BotonCopiaHistoria.tsx
// — misma lógica (generación audita ANTES de ofrecer la descarga,
// fail-closed), solo cambia la UI.
import { formatHumanName } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import type { Patient, Reference } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { reportarAccesoManual } from '../lib/audit/auditedFetch';
import { entregarCopia } from './vaultClient';
import { generarPdfCopiaHistoria } from './pdfCopiaHistoria';

export interface BotonCopiaHistoriaProps {
  readonly subject: Reference<Patient>;
}

type Estado = 'idle' | 'generando' | 'listo' | 'enviando' | 'error';

export function BotonCopiaHistoria({ subject }: BotonCopiaHistoriaProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado>('idle');
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  // Bug real encontrado en vivo 2026-09-27: el backend (entregarCopia/
  // sendConsentPdf) ya arma un `waLink` (wa.me con el mensaje precargado) de
  // respaldo cuando el envío se degrada ("skipped"/"error") — el mensaje le
  // decía al usuario "use el enlace directo" pero acá nunca se guardaba ni
  // se mostraba ningún link.
  const [waLink, setWaLink] = useState<string | null>(null);
  // Barra de progreso (2026-09-28, pedido explícito de Sue tras ver que
  // generar la copia de un historial grande tardaba minutos, secuencial) —
  // ver mapConcurrente/CONCURRENCIA_DESCARGA en pdfCopiaHistoria.ts, que ya
  // paraleliza las descargas; esto solo refleja ese avance en la UI.
  const [progreso, setProgreso] = useState<{ completados: number; total: number } | null>(null);

  async function generar(): Promise<void> {
    setEstado('generando');
    setMensaje(null);
    setWaLink(null);
    setProgreso(null);
    try {
      const patient = await medplum.readReference<Patient>(subject);
      const bytes = await generarPdfCopiaHistoria(medplum, patient, subject, (completados, total) => setProgreso({ completados, total }));
      const registrado = await reportarAccesoManual('hce-record-copy', subject.reference ?? 'Patient', 'Copia de historia clínica generada');
      if (!registrado) {
        throw new Error(t('consentimiento.copiaHistoria.error'));
      }
      setPdfBytes(bytes);
      setEstado('listo');
    } catch (err) {
      setMensaje(err instanceof Error ? err.message : String(err));
      setEstado('error');
    }
  }

  function descargar(): void {
    if (!pdfBytes) return;
    const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'historia-clinica.pdf';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function enviarWhatsapp(): Promise<void> {
    if (!pdfBytes) return;
    setEstado('enviando');
    try {
      const profile = medplum.getProfile();
      const resultado = await entregarCopia({
        patientRef: subject.reference ?? 'Patient',
        practitionerName: profile?.name?.[0] ? formatHumanName(profile.name[0]) : undefined,
        pdf: pdfBytes,
      });
      setMensaje(resultado.status === 'sent' ? t('consentimiento.copiaHistoria.enviado') : t('consentimiento.copiaHistoria.omitido'));
      setWaLink(resultado.status === 'sent' ? null : (resultado.waLink ?? null));
      setEstado('listo');
    } catch (err) {
      setMensaje(err instanceof Error ? err.message : String(err));
      setWaLink(null);
      setEstado('error');
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {mensaje && (
        <Alert variant={estado === 'error' ? 'red' : 'default'}>
          <AlertDescription>
            {mensaje}
            {waLink && (
              <>
                {' '}
                <a href={waLink} target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">
                  {t('consentimiento.copiaHistoria.enlaceDirecto')}
                </a>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
      <div className="flex items-center gap-2">
        <Button onClick={() => void generar()} disabled={estado === 'generando'} variant={pdfBytes ? 'outline' : 'default'}>
          {t('consentimiento.copiaHistoria.boton')}
        </Button>
        {pdfBytes && (
          <>
            <Button variant="secondary" onClick={descargar}>
              {t('consentimiento.copiaHistoria.descargar')}
            </Button>
            <Button variant="secondary" onClick={() => void enviarWhatsapp()} disabled={estado === 'enviando'}>
              {t('consentimiento.copiaHistoria.enviarWhatsapp')}
            </Button>
          </>
        )}
      </div>
      {estado === 'generando' && (
        <p className="text-sm text-muted-foreground">
          {progreso && progreso.total > 0 ? t('consentimiento.copiaHistoria.generandoProgreso', { completados: progreso.completados, total: progreso.total }) : t('consentimiento.copiaHistoria.generando')}
        </p>
      )}
    </div>
  );
}
